import {
  TRAINING_MODULE,
  HAZARDOUS_MATERIALS_TRAINING_EVALUATION,
  EQUIPMENT_MANAGEMENT_TRAINING_EVALUATION,
  EXTERNAL_EMERGENCY_PREPAREDNESS_TRAINING_EVALUATION,
} from "../../constants/modules";

export type TrainingModuleKey =
  | "fire_safety"
  | "hazardous_materials"
  | "equipment_management"
  | "external_emergency_preparedness";

export interface TrainingModulePaths {
  dashboard: string;
  createTraining: string;
  editTraining: (id: string) => string;
  viewTraining: (id: string) => string;
  trainingView: (id: string) => string;
  createTest: string;
  startTest: (id: string) => string;
  attemptTest: (id: string) => string;
  trainerStartTest: string;
  upcomingSessions: string;
  upcomingSessionDetails: (id: string) => string;
  pastEvaluations: string;
  pastEvaluationDetails: (id: string) => string;
  userAnalytics: string;
}

export interface TrainingModuleConfig {
  moduleType: TrainingModuleKey;
  /** Categories selectable when creating a training. A single entry means the category is fixed (read-only) in the UI. */
  validCategories: string[];
  defaultCategory: string;
  /** Permission key passed to PermissionWrapper / hasPermission(module, action) */
  rbacModule: string;
  /** Route prefix this module is mounted at */
  basePath: string;
  paths: TrainingModulePaths;
}

export const FIRE_SAFETY_TRAINING_CONFIG: TrainingModuleConfig = {
  moduleType: "fire_safety",
  validCategories: ["Fire Safety", "Health & Safety", "First Aid", "Food Safety"],
  defaultCategory: "Fire Safety",
  rbacModule: TRAINING_MODULE,
  basePath: "/trainings",
  paths: {
    dashboard: "/trainings/training-dashboard",
    createTraining: "/trainings/create-planing",
    editTraining: (id) => `/trainings/update-planing/${id}`,
    viewTraining: (id) => `/trainings/view/${id}`,
    trainingView: (id) => `/trainings/training-view/${id}`,
    createTest: "/trainings/create-test",
    startTest: (id) => `/trainings/start-test/${id}`,
    attemptTest: (id) => `/trainings/attempt-test/${id}`,
    trainerStartTest: "/trainings/trainer-start-test",
    upcomingSessions: "/trainings/upcoming-sessions",
    upcomingSessionDetails: (id) => `/trainings/upcoming-session-details/${id}`,
    pastEvaluations: "/trainings/past-evaluations",
    pastEvaluationDetails: (id) => `/trainings/past-evaluation-details/${id}`,
    userAnalytics: "/trainings/user-analytics",
  },
};

function nestedModuleConfig(
  moduleType: TrainingModuleKey,
  category: string,
  rbacModule: string,
  basePath: string,
): TrainingModuleConfig {
  return {
    moduleType,
    validCategories: [category],
    defaultCategory: category,
    rbacModule,
    basePath,
    paths: {
      dashboard: `${basePath}/dashboard`,
      createTraining: `${basePath}/trainings/create`,
      editTraining: (id) => `${basePath}/trainings/${id}/edit`,
      viewTraining: (id) => `${basePath}/trainings/${id}/view`,
      trainingView: (id) => `${basePath}/training-view/${id}`,
      createTest: `${basePath}/create-test`,
      startTest: (id) => `${basePath}/start-test/${id}`,
      attemptTest: (id) => `${basePath}/attempt-test/${id}`,
      trainerStartTest: `${basePath}/trainer-start-test`,
      upcomingSessions: `${basePath}/upcoming-sessions`,
      upcomingSessionDetails: (id) => `${basePath}/upcoming-sessions/${id}`,
      pastEvaluations: `${basePath}/past-evaluations`,
      pastEvaluationDetails: (id) => `${basePath}/past-evaluations/${id}`,
      userAnalytics: `${basePath}/user-analytics`,
    },
  };
}

export const HAZARDOUS_MATERIALS_TRAINING_CONFIG = nestedModuleConfig(
  "hazardous_materials",
  "Hazardous Materials",
  HAZARDOUS_MATERIALS_TRAINING_EVALUATION,
  "/hazardous-materials/staff-training-evaluation",
);

export const EQUIPMENT_MANAGEMENT_TRAINING_CONFIG = nestedModuleConfig(
  "equipment_management",
  "Equipment Management",
  EQUIPMENT_MANAGEMENT_TRAINING_EVALUATION,
  "/equipment-management/staff-training-evaluation",
);

export const EXTERNAL_EMERGENCY_PREPAREDNESS_TRAINING_CONFIG = nestedModuleConfig(
  "external_emergency_preparedness",
  "External Emergency Preparedness",
  EXTERNAL_EMERGENCY_PREPAREDNESS_TRAINING_EVALUATION,
  "/external-emergency-preparedness/staff-training-evaluation",
);
